// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

export type OctoEvaluateResult = components["schemas"]["OctoRulesEvaluateResponse"];
export type OctoEvaluatedRule = components["schemas"]["OctoRuleEvaluationResponse"];
export type OctoRuleEvaluation = components["schemas"]["OctoRuleEvaluationEnum"];
export type OctoEvaluateReason = components["schemas"]["OctoEvaluateReasonEnum"];
export type OctoRuleScope = components["schemas"]["OctoRuleScopeEnum"];

export interface OctoEvaluateParams {
  storeName: string;
  stage: components["schemas"]["OctoRuleStageEnum"];
  /** Omit to resolve the rule chain without running it — the flow with no verdicts on it. */
  taskType?: string;
  upc?: string;
  itemNumber?: string;
  /** Naive `YYYY-MM-DDTHH:mm:ss`, read by the API as store-local wall-clock time. */
  evaluatedAt?: string;
}

/**
 * Dry-run the octo rules for a store/stage. Read-only despite the POST: the endpoint replays the
 * decision the engine would reach and reports it rule by rule, without creating or suppressing
 * anything. Disabled until a store is picked.
 */
export function useOctoEvaluateQuery(params: OctoEvaluateParams, options: { enabled?: boolean } = {}) {
  const { storeName, stage, taskType, upc, itemNumber, evaluatedAt } = params;
  return useQuery({
    queryKey: ["octo", "evaluate", { storeName, stage, taskType, upc, itemNumber, evaluatedAt }],
    queryFn: async () => {
      const res = await api.POST("/v1/octo/rules/evaluate", {
        body: {
          storeName,
          stage,
          taskType: taskType || undefined,
          upc: upc || undefined,
          itemNumber: itemNumber || undefined,
          evaluatedAt: evaluatedAt || undefined,
        },
      });
      if (res.error) throw new Error("Failed to evaluate octo rules");
      return res.data as unknown as OctoEvaluateResult;
    },
    enabled: (options.enabled ?? true) && !!storeName,
    // A dry run reflects live rules and live inventory — never serve a stale one.
    staleTime: 0,
    retry: false,
  });
}
